import { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { supabase } from "@/lib/supabase";
import { verifyOtp, requestOtp, AuthError } from "@/lib/auth";

import { Button } from "@/components/ui/Button";
import { Field } from "@/components/ui/Field";
import { Input } from "@/components/ui/Input";
import { ErrorBlock, InlineAlert } from "@/components/ui/States";
import { MobileFrame } from "@/components/ui/MobileFrame";

export function VerifyCodePage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const email = params.get("email") ?? "";

  const [code, setCode] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resending, setResending] = useState(false);
  const [resentNotice, setResentNotice] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) {
      setError("Email is missing. Go back to the previous step.");
      return;
    }
    if (code.replace(/\D/g, "").length !== 6) {
      setError("Enter the 6-digit code from the email.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await verifyOtp(email, code.replace(/\D/g, ""));
      const { data: u } = await supabase.auth.getUser();
      if (!u.user) throw new AuthError("Session not established. Try again.");
      const { data: profile } = await supabase
        .from("users")
        .select("full_name")
        .eq("id", u.user.id)
        .maybeSingle();
      const needsOnboarding = !profile?.full_name || (profile.full_name as string).trim() === "";
      const next = slug ? `/e/${slug}/continue` : "/me/meetups";
      if (needsOnboarding) {
        navigate(`/onboarding?next=${encodeURIComponent(next)}`, { replace: true });
      } else {
        navigate(next, { replace: true });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Invalid code. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const onResend = async () => {
    if (!email) return;
    setResending(true);
    setResentNotice(null);
    setError(null);
    try {
      await requestOtp(email);
      setResentNotice("Code sent again.");
    } catch (err) {
      setError(err instanceof AuthError ? err.message : "Couldn't resend the code.");
    } finally {
      setResending(false);
    }
  };

  return (
    <MobileFrame>
      <form onSubmit={onSubmit} className="flex-1 px-4 py-6 animate-fade-in" autoComplete="off">
        <h1 className="text-[24px] font-bold tracking-tight text-[#1a1a1a]">Enter your code</h1>
        <p className="mt-2 text-[14px] text-[#6c757d]">
          We sent a 6-digit code to <span className="font-semibold text-[#1a1a1a]">{email || "your email"}</span>
        </p>

        {error ? <ErrorBlock title="Error" message={error} className="mt-5" /> : null}
        {resentNotice ? (
          <InlineAlert tone="success" className="mt-5 text-[13px]">
            {resentNotice}
          </InlineAlert>
        ) : null}

        <div className="mt-6 flex flex-col gap-4">
          <Field label="Code from the email" required>
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="\d{6}"
              maxLength={6}
              required
              autoFocus
            />
          </Field>

          <Button
            type="submit"
            loading={submitting}
            disabled={code.replace(/\D/g, "").length !== 6}
            fullWidth
          >
            {submitting ? "Verifying..." : "Confirm"}
          </Button>

          <button
            type="button"
            onClick={onResend}
            disabled={resending}
            className="mt-2 text-center text-[13px] font-semibold text-[#475569] hover:text-[#2563eb] disabled:opacity-50"
          >
            {resending ? "Sending..." : "Didn't get the email? Send again"}
          </button>
        </div>
      </form>
    </MobileFrame>
  );
}
